<template>
    <Modal
        id="cancel-order-modal"
        v-model:open="open"
        :title="title"
        @submit="submit"
        @cancel="cancel"
    >
        <div class="px-5">
            <span class="font-bold">
                {{ $t('id') }}: {{ modelValue.order_uid }}</span
            ><br />
            {{ $t('platform') }}: {{ modelValue.platform_name }}<br />
            {{ $t('account') }}: {{ modelValue.account_title }}
            <div
                v-if="isWithinCancelPeriod"
                class="mt-4 rounded border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800"
            >
                {{
                    $t('cancel_order_within_cancel_period_warning', {
                        deadline: formattedDeadline,
                    })
                }}
                <label class="mt-3 flex cursor-pointer items-start gap-2">
                    <input
                        v-model="policyConflictConfirmed"
                        type="checkbox"
                        class="mt-0.5 shrink-0 accent-yellow-700"
                    />
                    <span>{{
                        $t('cancel_order_policy_conflict_confirm')
                    }}</span>
                </label>
            </div>
            <div
                v-if="isSecupay"
                class="mt-4"
            >
                <ToggleSwitch
                    v-model="withRefund"
                    name="with_refund"
                    label="with_refund"
                    help="help_with_refund"
                />
            </div>
        </div>
        <template #actions>
            <button
                type="submit"
                class="btn-sm bg-danger-500 hover:bg-danger-600 text-white disabled:opacity-50"
                :disabled="isWithinCancelPeriod && !policyConflictConfirmed"
            >
                {{ $t('modal_text_order_status_set_order_status_canceled') }}
            </button>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { Modal, ToggleSwitch } from '@src/components'
import { Order } from '@src/types'
import { useVModels } from '@vueuse/core'
import { computed, ref } from 'vue'

const props = defineProps<{
    modelValue: Order
    open: boolean
    title: string
    toStatus: number
    isSecupay: boolean
}>()

const emit = defineEmits([
    'update:modelValue',
    'update:open',
    'submit',
    'cancel',
])

const { modelValue, open } = useVModels(props, emit)

const withRefund = ref(false)
const policyConflictConfirmed = ref(false)

const isWithinCancelPeriod = computed(() => {
    if (!modelValue.value.cancel_deadline) return false
    return new Date() < new Date(modelValue.value.cancel_deadline)
})

const formattedDeadline = computed(() => {
    if (!modelValue.value.cancel_deadline) return ''
    return new Date(modelValue.value.cancel_deadline).toLocaleDateString()
})

function cancel() {
    withRefund.value = false
    policyConflictConfirmed.value = false
    emit('cancel')
}

function submit() {
    emit('submit', modelValue.value, { withRefund: withRefund.value })
    withRefund.value = false
    policyConflictConfirmed.value = false
}
</script>
